import React from 'react';
import { Table } from 'antd';
import Text from 'antd/lib/typography/Text';
import {
  SessionTimeline,
  SessionTimelineLe,
  SessionTimelineStation,
  SessionTimelineProgress,
  SessionTimelineAnswer,
} from 'shared/types';
import { formatTime, parseISODateFormat } from 'utils/date-utils';

type Props = {
  timeline: SessionTimeline;
};

const tod = (v: string | null): string => (v ? parseISODateFormat(v, 'h:mm:ssa') : '—');
const dur = (v: number | null): string => (v != null ? formatTime(v) : '—');

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div style={{ marginBottom: 18 }}>
    <Text strong>{title}</Text>
    <div style={{ marginTop: 6 }}>{children}</div>
  </div>
);

/**
 * Raw logs behind the chart: the sessions_le, sessions_station, progress_reports
 * and answer_reports rows for this session, as plain tables.
 */
const SessionRawTables: React.FC<Props> = ({ timeline }): JSX.Element => {
  // Resolve friendly LE names so every table matches the chart / LE table
  // (station, progress and answer rows only carry the raw learning_experience_id).
  const nameByLeId = new Map<string, string>();
  for (const le of timeline.learningExperiences) {
    if (le.learning_experience_id && le.learning_experience_name) {
      nameByLeId.set(le.learning_experience_id, le.learning_experience_name);
    }
  }
  const leName = (id: string | null): string => (id ? nameByLeId.get(id) ?? `LE ${id}` : '—');

  const leData = timeline.learningExperiences.map(r => ({ key: r.id, ...r }));
  const stationData = [...timeline.stations]
    .sort((a, b) => (a.entered_at ?? '').localeCompare(b.entered_at ?? ''))
    .map(r => ({ key: r.id, ...r }));
  const progressData = timeline.progressReports.map(r => ({ key: r.id, ...r }));
  const answerData = [...timeline.answers]
    .sort((a, b) => (a.answered_at ?? '').localeCompare(b.answered_at ?? ''))
    .map(r => ({ key: r.id, ...r }));

  const leColumns = [
    { title: 'LE', key: 'le', render: (_: unknown, r: SessionTimelineLe): string => leName(r.learning_experience_id) },
    { title: 'Entered', key: 'in', render: (_: unknown, r: SessionTimelineLe): string => tod(r.entered_at) },
    { title: 'Exited', key: 'out', render: (_: unknown, r: SessionTimelineLe): string => tod(r.exited_at) },
    { title: 'Duration', key: 'dur', render: (_: unknown, r: SessionTimelineLe): string => dur(r.duration_seconds) },
    { title: 'LE session id', dataIndex: 'id', key: 'id', ellipsis: true },
  ];

  const stationColumns = [
    { title: 'Station', dataIndex: 'station_number', key: 'st', width: 80, render: (v: number | null): string | number => v ?? 'Intro' },
    { title: 'LE', key: 'le', render: (_: unknown, r: SessionTimelineStation): string => leName(r.learning_experience_id) },
    { title: 'Entered', key: 'in', render: (_: unknown, r: SessionTimelineStation): string => tod(r.entered_at) },
    { title: 'Exited', key: 'out', render: (_: unknown, r: SessionTimelineStation): string => tod(r.exited_at) },
    { title: 'Duration', key: 'dur', render: (_: unknown, r: SessionTimelineStation): string => dur(r.duration_seconds) },
    { title: 'LE session id', dataIndex: 'le_session_id', key: 'lesid', ellipsis: true },
  ];

  const progressColumns = [
    { title: 'LE', key: 'le', render: (_: unknown, r: SessionTimelineProgress): string => leName(r.learning_experience_id) },
    { title: 'Last station', dataIndex: 'last_completed_station', key: 'ls', width: 110, render: (v: number | null): string | number => v ?? 'Intro' },
    { title: 'Time in station', key: 'tis', render: (_: unknown, r: SessionTimelineProgress): string => dur(r.time_in_station_seconds) },
    { title: 'Time in LE', key: 'tile', render: (_: unknown, r: SessionTimelineProgress): string => dur(r.time_in_learning_experience_seconds) },
    { title: 'Completed at', key: 'cat', render: (_: unknown, r: SessionTimelineProgress): string => tod(r.last_completed_station_at) },
  ];

  const answerColumns = [
    { title: 'Station', dataIndex: 'station_number', key: 'st', width: 80, render: (v: number | null): string | number => v ?? '—' },
    { title: 'LE', key: 'le', render: (_: unknown, r: SessionTimelineAnswer): string => leName(r.learning_experience_id) },
    { title: 'Correct?', dataIndex: 'is_correct', key: 'ic', width: 90, render: (v: boolean | null): string => (v === true ? 'Correct' : v === false ? 'Incorrect' : '—') },
    { title: 'Answered at', key: 'aa', render: (_: unknown, r: SessionTimelineAnswer): string => tod(r.answered_at) },
  ];

  return (
    <div>
      <Section title={`Learning Experiences — sessions_le (${leData.length})`}>
        <Table size='small' columns={leColumns} dataSource={leData} pagination={false} scroll={{ x: true }} />
      </Section>
      <Section title={`Stations — sessions_station (${stationData.length})`}>
        <Table size='small' columns={stationColumns} dataSource={stationData} pagination={false} scroll={{ x: true }} />
      </Section>
      <Section title={`Progress Reports — progress_reports (${progressData.length})`}>
        <Table size='small' columns={progressColumns} dataSource={progressData} pagination={false} scroll={{ x: true }} />
      </Section>
      <Section title={`Answers — answer_reports (${answerData.length})`}>
        <Table size='small' columns={answerColumns} dataSource={answerData} pagination={false} scroll={{ x: true }} />
      </Section>
    </div>
  );
};

export default SessionRawTables;
